import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import {
  ArrowLeft, Bell, Plus, Trash2, ToggleLeft, ToggleRight,
  TrendingUp, TrendingDown, AlertTriangle, Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ALERT_TYPE_LABELS = {
  price_above: "Price Above",
  price_below: "Price Below",
  pct_drop: "% Drop (24h)",
  fear_greed: "Fear & Greed"
};

export default function PriceAlerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [alertType, setAlertType] = useState("price_above");
  const [alertValue, setAlertValue] = useState("");

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      const res = await api.get("/alerts/my-alerts");
      setAlerts(res.data.alerts);
    } catch (err) {}
    setLoading(false);
  };

  const createAlert = async () => {
    if (!alertValue) return;
    try {
      await api.post("/alerts/create", { alert_type: alertType, value: parseFloat(alertValue) });
      toast.success("Alert created!");
      setShowCreate(false);
      setAlertValue("");
      loadAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create alert");
    }
  };

  const deleteAlert = async (id) => {
    try {
      await api.delete(`/alerts/${id}`);
      setAlerts(alerts.filter(a => a.id !== id));
      toast.success("Alert deleted");
    } catch (err) {}
  };

  const toggleAlert = async (id) => {
    try {
      const res = await api.put(`/alerts/${id}/toggle`);
      setAlerts(alerts.map(a => a.id === id ? { ...a, enabled: res.data.enabled } : a));
    } catch (err) {}
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="price-alerts-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">PRICE <span className="text-[#F7931A]">ALERTS</span></h1>
          <Bell className="text-[#F7931A]" size={20} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-zinc-400 text-sm">Get notified when Bitcoin hits your targets.</p>
          <Button onClick={() => setShowCreate(!showCreate)} className="btn-primary text-xs" data-testid="add-alert-btn">
            <Plus size={14} className="mr-1" /> New Alert
          </Button>
        </div>

        {/* Create Form */}
        {showCreate && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            className="card-dark p-4 space-y-3" data-testid="create-alert-form"
          >
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(ALERT_TYPE_LABELS).map(([type, label]) => (
                <button key={type}
                  onClick={() => setAlertType(type)}
                  className={`p-2 rounded-lg text-xs text-center ${
                    alertType === type ? "bg-[#F7931A]/20 text-[#F7931A] border border-[#F7931A]/30" : "bg-zinc-800 text-zinc-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                value={alertValue}
                onChange={(e) => setAlertValue(e.target.value)}
                placeholder={alertType.includes("price") ? "Price in USD" : "Value"}
                className="input-dark flex-1"
                data-testid="alert-value-input"
              />
              <Button onClick={createAlert} className="btn-primary" data-testid="save-alert-btn">Save</Button>
            </div>
          </motion.div>
        )}

        {/* Alert List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-[#F7931A] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="text-zinc-700 mx-auto mb-3" size={40} />
            <p className="text-zinc-500 text-sm">No alerts set. Create your first price alert.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts.map((alert) => (
              <div key={alert.id} className={`card-dark p-4 flex items-center justify-between ${
                alert.triggered ? "border-green-500/30" : alert.enabled ? "" : "opacity-50"
              }`} data-testid={`alert-${alert.id}`}>
                <div className="flex items-center gap-3">
                  {alert.alert_type.includes("above") ? (
                    <TrendingUp className="text-green-400" size={18} />
                  ) : alert.alert_type.includes("below") ? (
                    <TrendingDown className="text-red-400" size={18} />
                  ) : (
                    <AlertTriangle className="text-[#F7931A]" size={18} />
                  )}
                  <div>
                    <p className="text-white text-sm font-medium">
                      {ALERT_TYPE_LABELS[alert.alert_type]} — {alert.alert_type.includes("price") ? `$${alert.value.toLocaleString()}` : alert.value}
                    </p>
                    <p className="text-zinc-500 text-xs">
                      {alert.triggered ? "Triggered!" : alert.enabled ? "Active" : "Paused"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleAlert(alert.id)} className="text-zinc-400 hover:text-white">
                    {alert.enabled ? <ToggleRight size={22} className="text-[#F7931A]" /> : <ToggleLeft size={22} />}
                  </button>
                  <button onClick={() => deleteAlert(alert.id)} className="text-zinc-400 hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
